import React, { useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import { BillingStatus } from 'services/EpdClient';
import CompanyService from 'services/CompanyService';
import { EpdLinks } from 'routes/EpdRoutes';
import { ManageCompanyTab } from 'components/company/manageCompanyNavConstants';
import styled from 'styled-components';
import { HelpBoldText } from 'styles/v2/Styles.styled';
import { HyperLink } from 'styles/Styles.styled';
import { useCurrentUserOrNullIfAnonymous } from 'hooks/useCurrentUser';
import { RoleName } from 'constants/constants';

const CSS_VAR = '--billing-banner-height';
const BILLING_STATUS_QUERY_KEY = 'billingStatus';

const ALLOWED_ROLES = [RoleName.EPD_OWNER, RoleName.ADMIN, RoleName.SYSTEM_ADMIN];

const BillingStatusBanner: React.FC = () => {
  const { companyId } = useContext(CompanyContext);
  const bannerRef = useRef<HTMLDivElement>(null);
  const user = useCurrentUserOrNullIfAnonymous();
  const location = useLocation();
  const queryClient = useQueryClient();

  const canSeeBanner =
    (user?.roles?.some((r) => ALLOWED_ROLES.includes(r.name as RoleName)) ||
      user?.memberships?.some((r) => r.companyId === companyId && ALLOWED_ROLES.includes(r.roleName as RoleName))) ??
    false;

  const { data: billingStatus } = useQuery({
    queryKey: [BILLING_STATUS_QUERY_KEY, companyId],
    queryFn: () => CompanyService.getCompany(companyId!).then((c) => c.billingStatus),
    enabled: !!companyId && canSeeBanner,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  // Invalidate on every navigation so billing status is always fresh when moving around the app
  useEffect(() => {
    if (companyId && canSeeBanner) {
      queryClient.invalidateQueries({ queryKey: [BILLING_STATUS_QUERY_KEY, companyId] });
    }
  }, [location.pathname, companyId]);

  const isVisible = canSeeBanner && billingStatus != null && billingStatus !== BillingStatus.Ok;

  useEffect(() => {
    const el = bannerRef.current;
    if (!el) {
      document.documentElement.style.setProperty(CSS_VAR, '0px');
      return;
    }
    const observer = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        document.documentElement.style.setProperty(CSS_VAR, `${el.offsetHeight}px`);
      });
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.setProperty(CSS_VAR, '0px');
    };
  }, [billingStatus]);

  if (!isVisible) {
    return null;
  }

  const invoicesUrl = companyId ? EpdLinks.manageCompany(companyId, 'invoiceList' as ManageCompanyTab) : undefined;

  const InvoicesLink = invoicesUrl ? <HyperLink href={invoicesUrl}>Invoices</HyperLink> : <span>Invoices</span>;

  if (billingStatus === BillingStatus.Enforcement) {
    return (
      <EnforcementBanner ref={bannerRef} role="alert">
        <HelpBoldText>Your account has unpaid invoices.</HelpBoldText> As a result, some of your EPDs have been depublished.
        Please review and settle the outstanding invoices under {InvoicesLink} to restore publication status. For assistance,
        please contact us.
      </EnforcementBanner>
    );
  }

  return (
    <WarningBanner ref={bannerRef} role="alert">
      <HelpBoldText>Your account has unpaid invoices.</HelpBoldText> Please review and settle them under {InvoicesLink} as
      soon as possible. Continued non-payment may result in the depublication of your EPDs. For assistance, please contact
      us.
    </WarningBanner>
  );
};

const BannerBase = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 11;
  padding: 0.75rem 1.5rem;
  ${(props) => props.theme.typography.bodyBody14Regular}
`;

const WarningBanner = styled(BannerBase)`
  background-color: ${(props) => props.theme.colors.statusLabelYellowBg};
  color: ${(props) => props.theme.colors.statusLabelYellowText};
`;

const EnforcementBanner = styled(BannerBase)`
  background-color: ${(props) => props.theme.colors.statusLabelRedBg};
  color: ${(props) => props.theme.colors.statusLabelRedText};
`;

export default BillingStatusBanner;
